import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Grid,
    Link,
    RadioGroup,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
    Button as MuiButton,
} from '@mui/material';

import { useGetRebookingInfo } from '../rebooking/hooks/use-get-rebooking-info';
import { useSetKeepReleaseRoomIntention } from '../rebooking/hooks/use-set-keep-release-room-intention';
import { RebookingDecision } from '../../utils/enum/RebookingDecision';
import RebookersLockup from '../../assets/png/rebook-lockup.png';
import { Button } from '../../components/button';
import { RebookingChoicesRadio } from '../rebooking-choices-modal/RebookingChoicesRadio';
import { getRebookingInfoUrl } from '../rebooking-choices-modal/get-rebooking-info-url';

import { RebookingDecisionSummaryCard } from './rebooking-decision-summary-card';

type RebookingChoicesCardProps = {
    developmentSlug: string;
};

export const RebookingChoicesCard: FC<RebookingChoicesCardProps> = ({ developmentSlug }) => {
    const { palette, breakpoints } = useTheme();
    const { t } = useTranslation();
    const { data, isLoading, refetch, isRefetching } = useGetRebookingInfo();
    const { mutate: setRebookingIntention, isLoading: isSettingRebookIntention } =
        useSetKeepReleaseRoomIntention();
    const [intention, setIntention] = useState<RebookingDecision | null>(null);
    const [changeIntention, setChangeIntention] = useState<boolean>(false);
    const isSmallScreen = useMediaQuery(breakpoints.down('md'));

    const toggleIntention = () =>
        setChangeIntention((currentChangeIntention) => !currentChangeIntention);

    if (isLoading || !data?.rebookingDecision) return null;

    const now = new Date();
    const start = new Date(data.rebookingDecision.start);
    const end = new Date(data.rebookingDecision.end);

    const isWithinDate = now >= start && now <= end;

    if (!isWithinDate) return null;

    if (data.rebookingDecision.intention === RebookingDecision.HOLD && !changeIntention)
        return (
            <RebookingDecisionSummaryCard
                toggleIntention={toggleIntention}
                title={t('rebooking_intention_card_title')}
                subtitle={t('rebooking_intention_card_subtitle-room_held')}
                info={t('rebooking_intention_card_info-room_held')}
            />
        );

    if (data.rebookingDecision.intention === RebookingDecision.RELEASE && !changeIntention)
        return (
            <RebookingDecisionSummaryCard
                toggleIntention={toggleIntention}
                title={t('rebooking_intention_card_title')}
                subtitle={t('rebooking_intention_card_subtitle-room_released')}
                info={t('rebooking_intention_card_info-room_released')}
            />
        );

    const rebookingInfoUrl = getRebookingInfoUrl(developmentSlug);

    return (
        <Stack
            p={2}
            css={{
                textDecoration: 'none',
            }}
            border="1px solid"
            borderColor={palette.grey[300]}
            spacing={2}
        >
            <Grid container p={2} bgcolor={palette.grey[100]} justifyContent="center">
                <Grid item xs={2} display={{ xs: 'none', md: 'inherit' }} alignItems="center">
                    <img alt="Rebookers Choices" src={RebookersLockup} height="56px" />
                </Grid>
                <Grid item xs={10} display="flex" alignItems="center">
                    <Stack justifyContent="center" spacing={1}>
                        <Typography variant="h3">{t('rebooking_intention_modal_title')}</Typography>
                        <Typography variant="caption" component="p" color={palette.grey[700]}>
                            {t('rebooking_intention_modal_subtitle')}
                        </Typography>
                    </Stack>
                </Grid>
            </Grid>

            <Grid container rowGap="8px">
                <RadioGroup
                    sx={{ width: '100%' }}
                    value={intention}
                    defaultValue={data.rebookingDecision.intention}
                >
                    <Grid container columnSpacing="16px" rowSpacing="16px" alignItems="stretch">
                        <Grid item xs={12} md={6}>
                            <RebookingChoicesRadio
                                label={t('rebooking_intention_modal_option_keep_label')}
                                description={t('rebooking_intention_card_description-room_held')}
                                checked={
                                    intention
                                        ? intention === RebookingDecision.HOLD
                                        : data.rebookingDecision.intention ===
                                          RebookingDecision.HOLD
                                }
                                onChange={() => setIntention(RebookingDecision.HOLD)}
                                value={RebookingDecision.HOLD}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RebookingChoicesRadio
                                label={t('rebooking_intention_modal_option_release_label')}
                                description={t(
                                    'rebooking_intention_card_description-room_released'
                                )}
                                checked={
                                    intention
                                        ? intention === RebookingDecision.RELEASE
                                        : data.rebookingDecision.intention ===
                                          RebookingDecision.RELEASE
                                }
                                onChange={() => setIntention(RebookingDecision.RELEASE)}
                                value={RebookingDecision.RELEASE}
                            />
                        </Grid>
                    </Grid>
                </RadioGroup>
            </Grid>

            <Grid container justifyContent="space-between" alignItems="center" rowGap={2}>
                <Grid
                    item
                    xs={12}
                    md="auto"
                    spacing="8px"
                    {...(isSmallScreen && {
                        display: 'flex',
                        justifyContent: 'center',
                    })}
                >
                    <b>
                        <Link href={rebookingInfoUrl} target="_blank">
                            {t('rebooking_intention_modal_find_out_more')}
                        </Link>
                    </b>
                </Grid>
                <Grid item xs={12} md="auto">
                    <Stack direction={{ xs: 'column-reverse', md: 'row' }} columnGap={2} rowGap={2}>
                        {data.rebookingDecision.intention && (
                            <MuiButton variant="text" onClick={() => setChangeIntention(false)}>
                                Cancel
                            </MuiButton>
                        )}
                        <Button
                            design="primary"
                            loading={isSettingRebookIntention || isRefetching}
                            disabled={!intention || intention === data.rebookingDecision.intention}
                            fullWidth={isSmallScreen}
                            onClick={() => {
                                if (!intention) return;

                                setRebookingIntention(
                                    { intention },
                                    {
                                        onSuccess() {
                                            refetch().then(() => {
                                                setChangeIntention(false);
                                            });
                                        },
                                    }
                                );
                            }}
                        >
                            {t('rebooking_intention_modal_confirm_choice')}
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Stack>
    );
};
